"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileProcessingResultSchema = exports.FileDerivativeSchema = exports.ProjectFileMetadataSchema = exports.FileUploadNotificationSchema = exports.UploadTokenPayloadSchema = exports.UploadTicketSchema = exports.UploadTicketRequestSchema = exports.FileInfoSchema = void 0;
const zod_1 = require("zod");
const usage_1 = require("./usage");
const mime_1 = require("./mime");
const s3_1 = require("./s3");
// Shared Zod schemas for upload requests, tickets, JWT payloads, file
// metadata, and derivative-processing results. Every derived type uses
// `z.infer<...>` so type and runtime schema never drift (constitution IV).
exports.FileInfoSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(), // uuid — primary key
    name: zod_1.z.string().min(1).max(s3_1.S3_PATH_CONSTRAINTS.MAX_FILENAME_LENGTH),
    status: zod_1.z.enum([usage_1.FileStatus.PROCESSING, usage_1.FileStatus.COMPLETE, usage_1.FileStatus.ERROR]),
    size: zod_1.z.number().positive().optional(),
    mimeType: zod_1.z.string().refine((val) => val in mime_1.ALLOWED_MIME_TYPES).optional(),
    etag: zod_1.z.string().optional(),
    uploadedBy: zod_1.z.number().int().positive().optional(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
    metadata: zod_1.z.record(zod_1.z.unknown()).optional(),
    enabled: zod_1.z.boolean().default(true),
    thumbnail: zod_1.z.boolean().default(false),
});
exports.UploadTicketRequestSchema = zod_1.z.object({
    projectId: zod_1.z.preprocess((v) => (v === null || v === undefined || v === '' ? undefined : v), zod_1.z.coerce.number().int().positive().optional()),
    urbanAnalysisId: zod_1.z.preprocess((v) => (v === null || v === undefined || v === '' ? undefined : v), zod_1.z.coerce.number().int().positive().optional()),
    filename: zod_1.z.string()
        .min(1, 'Filename cannot be empty')
        .max(s3_1.S3_PATH_CONSTRAINTS.MAX_FILENAME_LENGTH, 'Filename too long')
        .regex(s3_1.S3_PATH_CONSTRAINTS.FILENAME_PATTERN, 'Invalid characters in filename'),
    mimeType: zod_1.z.string().refine((val) => val in mime_1.ALLOWED_MIME_TYPES, {
        message: 'Unsupported file type',
    }),
    fileSize: zod_1.z.number().positive(),
    // Usage is optional: an absent/empty usage marks an *unassigned* upload
    // (the generic drop zone). Such files are linked with `use = NULL` and live
    // in the to-sort inbox until triaged. When present, it must be a valid DB
    // usage (`UNDEFINED` is itself a real category — "Autres documents").
    usage: zod_1.z.preprocess((v) => (v === null || v === undefined || v === '' ? undefined : v), zod_1.z.string().refine((val) => Object.values(usage_1.DbProjectFileUsage).includes(val), {
        message: 'Invalid file usage type',
    }).optional()),
    // Frontend-only: GraphQL mutation carries section alongside usage. Backend
    // accepts it but doesn't use it for validation. Kept optional for compat —
    // Hasura action transport serialises `section: undefined` as `null`, so we
    // accept both nullable and optional.
    section: zod_1.z.string().nullable().optional(),
});
exports.UploadTicketSchema = zod_1.z.object({
    uploadUrl: zod_1.z.string().url(),
    fileKey: zod_1.z.string(),
    token: zod_1.z.string(),
    expiresAt: zod_1.z.string().datetime(),
    maxSize: zod_1.z.number().positive(),
});
exports.UploadTokenPayloadSchema = zod_1.z.object({
    fileId: zod_1.z.string().uuid(),
    projectId: zod_1.z.number().int().positive().nullish(),
    urbanAnalysisId: zod_1.z.number().int().positive().nullish(),
    userId: zod_1.z.number().int().positive(),
    filename: zod_1.z.string(),
    mimeType: zod_1.z.string(),
    maxSize: zod_1.z.number().positive(),
    // Optional: an unassigned upload (generic drop zone) carries no usage and is
    // linked with `use = NULL`.
    usage: zod_1.z.string().optional(),
    exp: zod_1.z.number().int().positive(),
    iat: zod_1.z.number().int().positive(),
});
exports.FileUploadNotificationSchema = zod_1.z.object({
    fileId: zod_1.z.string().uuid(),
    fileKey: zod_1.z.string(),
    token: zod_1.z.string(),
    etag: zod_1.z.string().nullable().optional(),
    actualSize: zod_1.z.number().positive().optional(),
});
exports.ProjectFileMetadataSchema = zod_1.z.object({
    projectId: zod_1.z.number().int().positive(),
    fileId: zod_1.z.string().uuid(),
    usage: zod_1.z.string().optional(),
    status: zod_1.z.enum(['ACCEPTED', 'REJECTED']).optional(),
    accountVisibility: zod_1.z.string().optional(),
    comment: zod_1.z.string().optional(),
});
exports.FileDerivativeSchema = zod_1.z.object({
    type: zod_1.z.enum(['thumbnail', 'pdf-optimized', 'watermarked']),
    key: zod_1.z.string(),
    width: zod_1.z.number().positive().optional(),
    height: zod_1.z.number().positive().optional(),
    format: zod_1.z.string(),
    size: zod_1.z.number().positive(),
});
exports.FileProcessingResultSchema = zod_1.z.object({
    fileId: zod_1.z.string().uuid(),
    originalKey: zod_1.z.string(),
    derivatives: zod_1.z.array(exports.FileDerivativeSchema),
    metadata: zod_1.z.object({
        width: zod_1.z.number().positive().optional(),
        height: zod_1.z.number().positive().optional(),
        format: zod_1.z.string().optional(),
        colorSpace: zod_1.z.string().optional(),
        hasAlpha: zod_1.z.boolean().optional(),
        exif: zod_1.z.record(zod_1.z.unknown()).optional(),
    }),
    processingStatus: zod_1.z.enum(['success', 'partial', 'failed']),
    errors: zod_1.z.array(zod_1.z.string()).optional(),
});
//# sourceMappingURL=schemas.js.map